import { useMemo, useRef, useState } from "react";
import { PageHeader } from "@/components/PageHeader";
import { AreaTabs, PAYROLL_TABS } from "@/components/AreaTabs";
import { StateMessage } from "@/components/StateMessage";
import { Why } from "@/components/Why";
import { W4Dialog } from "@/components/W4Dialog";
import { EmployeeFormDialog } from "@/components/EmployeeFormDialog";
import { RateHistoryDialog } from "@/components/RateHistoryDialog";
import { PieceTypesPanel } from "@/components/PieceTypesPanel";
import { PieceTicketsPanel } from "@/components/PieceTicketsPanel";
import { useSelectedEntity } from "@/hooks/useSelectedEntity";
import { usePayrollRoster } from "@/hooks/usePayrollRoster";
import { useAnnualFuta } from "@/hooks/useAnnualFuta";
import { useH2aExemption } from "@/hooks/useH2aExemption";
import type { PayrollEmployee } from "@/api/types";
import { formatMoney } from "@/lib/format";
import { PAY_PERIOD_LABEL } from "@/pages/payroll-fixtures";
import "./H2a.css";

/**
 * H-2A workers — the second half of "the compliance work is real".
 *
 * Item 4 on Christy's list (docs/demo/tradeshow-2026-09.md, "Static booth material")
 * pairs an H-2A screen with a Paid Leave one. Paid Leave got a screen; H-2A did not,
 * and the reason it looked done is that the WORK is done — the exemption is computed,
 * tested and rendered in four separate places on /year-end: the 943's excluded
 * headcount, the 940's line 4 dollars, the W-2 rows' badges and blank Box 3, and the
 * 941's wrong-roster guard.
 *
 * <p>
 * WHAT WAS ACTUALLY MISSING IS A PLACE TO POINT AT. YearEnd.tsx is 806 lines behind
 * two switchers — one for the business, one for the return — so telling the H-2A story
 * at a booth meant navigating to Payroll returns, selecting the 943, reading, selecting
 * the 940, reading, then selecting W-2 to show the badges. Four navigations, in front of
 * a prospect, for one idea. This screen is that idea on one page.
 * </p>
 *
 * <p>
 * IT COMPUTES NOTHING THE RETURNS DO NOT ALREADY COMPUTE. The exemption headline's
 * figures come from the same two calls those screens make — POST /api/payroll/run
 * (useH2aExemption) and POST /api/payroll/annual-futa (useAnnualFuta). The
 * exempt-payments dollars are `form940.paymentsExemptFromFuta` verbatim; the headcount
 * is `form940.futaExemptEmployeeCount`. A second implementation of either would be a
 * second chance to disagree with the return a farmer actually files.
 * </p>
 *
 * <p>
 * THE ONE FIGURE THAT IS NOT ON THE RETURNS is what the exemption is worth, and it is
 * asked of the engine rather than derived — see useH2aExemption. It exists because
 * "$0.00 withheld" is true, correct, and completely mute about why anyone should care.
 * </p>
 *
 * <p>
 * ef-038-6 — THE ROSTER BELOW IS EVERYONE, NOT JUST THE EXEMPT ONES. GET
 * /api/demo/employees (usePayrollRoster) now feeds a table of every employee on the
 * entity — name, pay basis, rate, an H-2A badge on exempt rows only, and each row's own
 * W-4 status — so the screen answers "who is on payroll and where do they stand" as well
 * as "who is exempt and what does it save". The exemption headline card stays gated on
 * `h2aCount > 0`; the roster table is not — see the `h2aCount > 0` guard below is scoped
 * to the headline/returns/disclosure sections only, never the table.
 * </p>
 */
export function H2a({
  onNavigate,
}: {
  /**
   * The second argument names WHICH return to open — see YearEndProps.initialView. Without
   * it the three buttons below all landed on the 943, so two of them named a return and
   * showed a different one.
   */
  onNavigate?: (path: string, view?: "return" | "940" | "w2") => void;
}) {
  const roster = usePayrollRoster("primary");
  const futa = useAnnualFuta(roster.employees);

  /**
   * MEMOISED, and not as a micro-optimisation. `useH2aExemption` keys its effect on
   * this array's identity; a fresh `.filter()` on every render would hand it a new
   * array every time and fire an endless run of payroll calls at the engine.
   */
  const h2aEmployees = useMemo(
    () => (roster.employees === null ? null : roster.employees.filter((e) => e.ficaExemptByLaw)),
    [roster.employees],
  );

  const exemption = useH2aExemption(h2aEmployees);

  // ---- Set W-4 dialog (ef-038-4) --------------------------------------------------------
  // The dialog itself is W4Dialog (components/W4Dialog.tsx) — a SIBLING of the table, not a
  // second row inside it, so h2a.spec.ts's discrimination test (which filters rows by
  // employee NAME with `toHaveCount(1)`) is untouched: a detail row repeating that name
  // inside the same <table> would push the count to 2 and fail a protected test. This page
  // only tracks WHICH employee's dialog is open and where to return focus when it closes —
  // W4Dialog owns its own form state, validation, and the PATCH call.
  const [w4Employee, setW4Employee] = useState<PayrollEmployee | null>(null);
  const w4TriggerRef = useRef<HTMLButtonElement | null>(null);

  function openW4(employee: PayrollEmployee, trigger: HTMLButtonElement): void {
    w4TriggerRef.current = trigger;
    setW4Employee(employee);
  }

  function closeW4(): void {
    setW4Employee(null);
    // Focus returns to the row's own trigger, same rule every dialog in this app follows
    // (BoothReset, JobCodes) — a keyboard user who closes a dialog must not be dropped back
    // at the top of the document.
    w4TriggerRef.current?.focus();
  }

  // ---- Add person / Edit / Rate history (ef-058-4) ---------------------------------------
  // Same sibling-dialog arrangement as Set W-4 above: this page only tracks WHICH dialog is
  // open and where focus returns; the dialogs own their form state and API calls. A save
  // refetches the roster so the table (and everything reading it) shows the server's truth.
  const [form, setForm] = useState<{ employee?: PayrollEmployee } | null>(null);
  const [historyEmployee, setHistoryEmployee] = useState<PayrollEmployee | null>(null);
  const dialogTriggerRef = useRef<HTMLButtonElement | null>(null);
  const { entityId: selectedEntityId } = useSelectedEntity();
  // A new person joins the entity this roster IS (the one employing the most people), so
  // they appear here; an empty roster falls back to the selected entity.
  const rosterEntityId = roster.employees?.[0]?.entityId ?? selectedEntityId;

  function openForm(trigger: HTMLButtonElement, employee?: PayrollEmployee): void {
    dialogTriggerRef.current = trigger;
    setForm(employee ? { employee } : {});
  }

  function openHistory(employee: PayrollEmployee, trigger: HTMLButtonElement): void {
    dialogTriggerRef.current = trigger;
    setHistoryEmployee(employee);
  }

  function closeEmployeeDialog(): void {
    setForm(null);
    setHistoryEmployee(null);
    dialogTriggerRef.current?.focus();
  }

  const rosterSize = roster.employees?.length ?? 0;
  const h2aCount = h2aEmployees?.length ?? 0;
  const plural = h2aCount === 1 ? "worker" : "workers";
  /*
    THE POSSESSIVE IS ITS OWN FORM, not the plural with an apostrophe-s bolted on.
    This rendered `{plural}&apos;s`, which is right for one worker and produces
    **"these 4 workers's checks"** for any other number — and any other number is the normal
    case: the demo roster carries four. Found on 2026-09-07 by reading the H-2A screen the way
    a visitor does, on a page whose whole credibility rests on looking like it was written by
    someone who knows payroll.
  */
  const possessive = h2aCount === 1 ? "worker's" : "workers'";

  return (
    <main className="h2a">
      <PageHeader
        title="People"
        subtitle={
          roster.status === "ready"
            ? `Sample pay period ${PAY_PERIOD_LABEL} · ${h2aCount} of ${rosterSize} on the roster`
            : `Sample pay period ${PAY_PERIOD_LABEL}`
        }
      />

      <AreaTabs area="/payroll" tabs={PAYROLL_TABS} label="Payroll" />

      {roster.status === "ready" && (
        <div className="h2a__toolbar">
          <button type="button" className="btn btn--primary" onClick={(e) => openForm(e.currentTarget)}>
            Add person
          </button>
        </div>
      )}

      {roster.status === "error" && (
        <StateMessage
          tone="error"
          title="Could not load the payroll roster."
          detail={`${roster.error ?? ""} Nothing below is computed — there is no honest answer about who is exempt without knowing who is on the payroll.`}
        />
      )}

      {roster.status === "loading" && <StateMessage tone="loading" title="Loading the roster…" />}

      {roster.status === "ready" && rosterSize === 0 && (
        <StateMessage
          tone="empty"
          title="Nobody is on this entity's payroll yet."
          detail="An empty roster is a real answer, not a failure — there is no one to be exempt."
        />
      )}

      {roster.status === "ready" && rosterSize > 0 && (
        <>
          {/* ── the point of the screen, in two numbers — gated on h2aCount ALONE.
              ef-038-6: this card is the only thing that disappears when nobody on the
              roster is H-2A. The table below is everyone's, regardless. ─────────── */}
          {h2aCount > 0 && (
            <section className="h2a__headline" aria-labelledby="h2a-headline-heading">
              <h2 id="h2a-headline-heading" className="h2a__card-title">
                Two exemptions, one group of people
              </h2>

              <dl className="h2a__figures">
                {/*
                  The explanatory note lives INSIDE the <dd>, not beside it. A `dl > div`
                  group may contain only <dt> and <dd>; a sibling <p> is invalid and axe
                  fails it (definition-list / only-dlitems). The note is part of what the
                  term's description IS, so nesting it is also the more accurate markup —
                  not merely the one that passes.
                */}
                <div className="h2a__figure">
                  <dt>Exempt from Social Security &amp; Medicare</dt>
                  <dd>
                    <span className="h2a__amount num">
                      {exemption.status === "ready" && exemption.wouldBeWithheld !== null ? (
                        formatMoney(exemption.wouldBeWithheld)
                      ) : exemption.status === "error" ? (
                        <span className="h2a__unknown">unavailable</span>
                      ) : (
                        <span className="h2a__unknown">calculating…</span>
                      )}
                    </span>
                    <p className="h2a__figure-note">
                      What these {h2aCount} {possessive} checks would lose without the exemption.
                    </p>
                    <Why>
                      <p>
                        The engine was asked directly — this is a second payroll run over the
                        same people with the exemption switched off, not a rate applied to a
                        wage figure on this screen.
                      </p>
                    </Why>
                  </dd>
                </div>

                <div className="h2a__figure">
                  <dt>Exempt from federal unemployment tax</dt>
                  <dd>
                    <span className="h2a__amount num">
                      {futa.status === "ready" && futa.form ? (
                        formatMoney(futa.form.form940.paymentsExemptFromFuta)
                      ) : futa.status === "error" ? (
                        <span className="h2a__unknown">unavailable</span>
                      ) : (
                        <span className="h2a__unknown">calculating…</span>
                      )}
                    </span>
                    <p className="h2a__figure-note">
                      Wages sitting on line 4 of the Form 940 projection.
                    </p>
                    <Why>
                      <p>
                        H-2A wages are exempt from FUTA as well as from FICA — two separate
                        exemptions that happen to cover the same people, reported exactly as
                        that return does.
                      </p>
                    </Why>
                  </dd>
                </div>
              </dl>

              {exemption.status === "error" && (
                <StateMessage
                  tone="error"
                  title="Could not price the FICA exemption."
                  detail={`${exemption.error ?? ""} The roster below is still the engine's, and so is the FUTA figure — only the "what it would cost" comparison is missing.`}
                />
              )}

              <p className="h2a__prose">
                Not Social Security or Medicare wages — but the exemption does not extend to
                income-tax withholding.
              </p>
              <Why>
                <p>
                  H-2A agricultural wages are <strong>not Social Security or Medicare wages at
                  all</strong>, so they are not reportable on those lines — not merely untaxed.
                  Federal income tax withheld <em>is</em> still included.
                </p>
              </Why>
            </section>
          )}

          {/* ── the full roster, by name — ef-038-6: every employee, not only the
              H-2A-exempt ones. Rate/W-4 status per row, badge on exempt rows only. ── */}
          <section className="h2a__roster" aria-labelledby="h2a-roster-heading">
            <h2 id="h2a-roster-heading" className="h2a__card-title">
              Everyone on this payroll
            </h2>

            {/*
              The table scrolls inside its own box rather than pushing the page sideways.
              Five columns do not fit a 375px phone, and the alternative — dropping a
              column at narrow widths — would hide either the badge that identifies these
              people or the withholding figure that makes the exemption's limit visible.
              overflow-x alone is not enough: a grid/flex item's automatic minimum size is
              its MIN-CONTENT width, so the wrapper needs min-width: 0 to be allowed to be
              narrower than the table it holds. Same construction as YearEnd's W-2 table.
            */}
            {/*
              tabIndex and a group role are what make that scroll box reachable without a
              mouse. A keyboard or switch user has no other way to move it, so the columns
              past the fold — the withholding figure among them — would simply not exist
              for them. axe fails this as `scrollable-region-focusable`; it is a real
              exclusion, not a lint.
            */}
            <div
              className="h2a__table-wrap"
              tabIndex={0}
              role="group"
              aria-labelledby="h2a-roster-heading"
            >
            <table className="h2a__table ruled-rows">
              <caption className="visually-hidden">
                Every employee on this payroll, with pay basis, rate, H-2A status and W-4
                election status per row
              </caption>
              <thead>
                <tr>
                  <th scope="col">#</th>
                  <th scope="col">Name</th>
                  <th scope="col">Pay basis</th>
                  <th scope="col" className="num">
                    Rate
                  </th>
                  <th scope="col">W-4</th>
                  <th scope="col">
                    <span className="visually-hidden">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {(roster.employees ?? []).map((employee) => (
                  <tr key={employee.id} data-active={employee.active === false ? "false" : "true"}>
                    <td>{employee.number}</td>
                    <td>
                      {employee.name}
                      {employee.ficaExemptByLaw && <span className="h2a__badge">H-2A</span>}
                      {employee.active === false && <span className="h2a__badge h2a__badge--inactive">Inactive</span>}
                    </td>
                    <td>{employee.payBasis === "salary" ? "Salaried" : "Hourly"}</td>
                    {/*
                      NULL means NOT SET, not zero (api/types.ts). An hourly employee
                      missing a rate reads "Not set", never $0.00 — a finalize with that
                      gap is refused (0030_pay_run_finalize.sql), and this column is the
                      one place that refusal is visible before a run is even attempted.
                      A salaried employee's pay is `grossWages`, a different figure this
                      column is not asking for, so it prints nothing rather than borrow it.
                    */}
                    <td className="num">
                      {employee.payBasis === "hourly" ? (
                        employee.hourlyRate !== null ? (
                          formatMoney(employee.hourlyRate)
                        ) : (
                          <span className="h2a__unknown">Not set</span>
                        )
                      ) : (
                        <span className="h2a__unknown" aria-hidden="true">
                          —
                        </span>
                      )}
                    </td>
                    <td>
                      {employee.w4Year !== null ? "Set" : <span className="h2a__unknown">Not set</span>}
                    </td>
                    <td>
                      <div className="h2a__actions">
                        <button
                          type="button"
                          className="btn btn--ghost"
                          onClick={(e) => openW4(employee, e.currentTarget)}
                        >
                          Set W-4
                        </button>
                        <button
                          type="button"
                          className="btn btn--ghost"
                          onClick={(e) => openForm(e.currentTarget, employee)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="btn btn--ghost"
                          onClick={(e) => openHistory(employee, e.currentTarget)}
                        >
                          Rate history
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </section>

          {/* ── where this shows up on what actually gets filed — gated with the
              headline card above; nothing to point at once nobody is H-2A. ─────── */}
          {h2aCount > 0 && onNavigate && (
            <section className="h2a__returns" aria-labelledby="h2a-returns-heading">
              <h2 id="h2a-returns-heading" className="h2a__card-title">
                Where this lands on the returns
              </h2>
              <p className="h2a__prose">
                Nothing on this screen is filed. These are the three places the same
                exemption reaches paper, each computed by the engine that produced the
                figures above.
              </p>
              <nav className="h2a__links" aria-label="Federal returns">
                <button
                  type="button"
                  className="h2a__link"
                  onClick={() => onNavigate("/payroll/returns")}
                >
                  <span className="h2a__link-label">
                    Form 943 — these {plural} excluded from the wage lines
                  </span>
                  <span aria-hidden="true">&rarr;</span>
                </button>
                <button
                  type="button"
                  className="h2a__link"
                  onClick={() => onNavigate("/payroll/returns", "940")}
                >
                  <span className="h2a__link-label">
                    Form 940 — line 4, payments exempt from FUTA
                  </span>
                  <span aria-hidden="true">&rarr;</span>
                </button>
                <button
                  type="button"
                  className="h2a__link"
                  onClick={() => onNavigate("/payroll/returns", "w2")}
                >
                  <span className="h2a__link-label">W-2s — badged, with Box 3 correctly blank</span>
                  <span aria-hidden="true">&rarr;</span>
                </button>
              </nav>
            </section>
          )}

          {/* ── the gap, said rather than filled in — same gate: this is what the
              H-2A badge doesn't decide, so it has nothing to say when there is no
              badge on the roster. ────────────────────────────────────────────── */}
          {h2aCount > 0 && (
            <section className="h2a__disclosure" aria-labelledby="h2a-disclosure-heading">
              <h2 id="h2a-disclosure-heading" className="h2a__card-title">
                What this badge does not decide
              </h2>
              <p className="h2a__prose">
                The H-2A flag stays out of MN Paid Leave — that question is legally open, not
                decided here.
              </p>
              <Why>
                <p>
                  The flag drives the <strong>FICA</strong> and <strong>FUTA</strong> exemptions
                  above, both settled federal rules. It is deliberately not forwarded to the MN
                  Paid Leave calculation — knowing who is H-2A does not make that determination,
                  and wiring it through would assert an answer nobody has given. The Paid Leave
                  screen states the same gap from its own side.
                </p>
              </Why>
              {onNavigate && (
                <nav className="h2a__links" aria-label="Related screens">
                  <button
                    type="button"
                    className="h2a__link"
                    onClick={() => onNavigate("/paid-leave")}
                  >
                    <span className="h2a__link-label">MN Paid Leave</span>
                    <span aria-hidden="true">&rarr;</span>
                  </button>
                </nav>
              )}
            </section>
          )}

          {/* ── Set W-4 (ef-038-4) ────────────────────────────────────────────────── */}
          {w4Employee && (
            <W4Dialog key={w4Employee.id} employee={w4Employee} onClose={closeW4} />
          )}
        </>
      )}

      {/* ── Piece pay (ef-060 anchors) — stubs until ef-060-6 / ef-060-7 replace them; outside the
          rosterSize gate like the dialogs below. Panels use LISTS, never a second <table>: the
          roster is the only table on this page (people*.spec.ts locate it as the page's one table). ── */}
      <PieceTypesPanel />
      <PieceTicketsPanel />

      {/* ── Add person / Edit / Rate history (ef-058-4) — outside the rosterSize gate so
          Add person still works on an empty roster. ─────────────────────────────── */}
      {form && (
        <EmployeeFormDialog
          key={form.employee?.id ?? "new"}
          employee={form.employee}
          entityId={rosterEntityId}
          onClose={closeEmployeeDialog}
          onSaved={roster.refetch}
        />
      )}
      {historyEmployee && (
        <RateHistoryDialog
          key={historyEmployee.id}
          employee={historyEmployee}
          onClose={closeEmployeeDialog}
          onChanged={roster.refetch}
        />
      )}
    </main>
  );
}
